import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type {
  IUserSocnets,
  IReduxRequestNode,
  ISocial,
  SIDPayload,
} from "@litres-libs/types";
import { Request } from "../../request/Request";

import { addHydrateReducer } from "../../utils/hydrateReducer";

interface IUserSocnetsState extends IUserSocnets {
  actions: {
    attachSocnetCat2: IReduxRequestNode<
      TAuthorizeSocnetParams,
      IAuthorizeSocnetPayload
    >;
    detachSocnetCat2: IReduxRequestNode<
      TDetachSocnetParams,
      IDetachSocnetPayload
    >;
  };
}

const initialState: IUserSocnetsState = {
  attachedSocnets: [],
  actions: {
    attachSocnetCat2: {},
    detachSocnetCat2: {},
  },
};

// W_ATTACH_SOCNET
export interface TAuthorizeSocnetParams {
  /** социальная сеть, через которую производится авторизация */
  socnet: ISocial;
  /** одноразовый ключ, полученный от социальной сети и подтверждающий возможность авторизации */
  token: string;
  /** одноразовый ключ, полученный от социальной сети и подтверждающий возможность авторизации */
  code?: string;
  /** дополнительные данные, необходимые для авторизации через токен */
  token_verifier?: string;
}

export interface IAuthorizeSocnetPayload
  extends SIDPayload,
    Record<string, unknown> {
  status: string;
}

export const attachSocnetCat2 = createAsyncThunk(
  "user/socnet/attachSocnetCat2",
  // if you type your function argument here
  async (actionParams: TAuthorizeSocnetParams) => {
    const response = new Request.Cat2<
      TAuthorizeSocnetParams,
      IAuthorizeSocnetPayload
    >({
      url: "/",
      provider: {
        id: "W_ATTACH_SOCNET",
        func: "w_attach_socnet",
      },
      request: {
        method: "POST",
        params: actionParams,
      },
    });

    return await response.fetch();
  }
);

// W_DETACH_SOCNET
export interface TDetachSocnetParams {
  socnet: ISocial;
}
export interface IDetachSocnetPayload extends Record<string, unknown> {
  success: string;
}

export const detachSocnetCat2 = createAsyncThunk(
  "user/socnet/detachSocnetCat2",
  // if you type your function argument here
  async (actionParams: TDetachSocnetParams) => {
    const response = new Request.Cat2<
      TDetachSocnetParams,
      IDetachSocnetPayload
    >({
      url: "/",
      provider: {
        id: "W_DETACH_SOCNET",
        func: "w_detach_socnet",
      },
      request: {
        method: "POST",
        params: actionParams,
      },
    });

    return await response.fetch();
  }
);

export const socnetSlice = createSlice({
  name: "user/socnet",
  initialState,
  reducers: {
    setUserSocnets: (
      state: IUserSocnetsState,
      action: PayloadAction<IUserSocnets>
    ) => {
      state.attachedSocnets = action.payload.attachedSocnets;
    },
  },
  extraReducers: builder => {
    addHydrateReducer(builder, "user.socnet");
    // Загрузка
    builder.addCase(attachSocnetCat2.pending, (state: IUserSocnetsState) => {
      state.actions.attachSocnetCat2.isLoading = true;
      state.actions.attachSocnetCat2.error = undefined;
    });
    // Ответ
    builder.addCase(
      attachSocnetCat2.fulfilled,
      (
        state: IUserSocnetsState,
        action: PayloadAction<
          IReduxRequestNode<TAuthorizeSocnetParams, IAuthorizeSocnetPayload>
        >
      ) => {
        state.actions.attachSocnetCat2 = action.payload;
        state.actions.attachSocnetCat2.isLoading = false;
      }
    );

    // Загрузка
    builder.addCase(detachSocnetCat2.pending, (state: IUserSocnetsState) => {
      state.actions.detachSocnetCat2.isLoading = true;
      state.actions.detachSocnetCat2.error = undefined;
    });
    // Ответ
    builder.addCase(
      detachSocnetCat2.fulfilled,
      (
        state: IUserSocnetsState,
        action: PayloadAction<
          IReduxRequestNode<TDetachSocnetParams, IDetachSocnetPayload>
        >
      ) => {
        state.actions.detachSocnetCat2 = action.payload;
        state.actions.detachSocnetCat2.isLoading = false;
      }
    );
  },
});

export const { setUserSocnets } = socnetSlice.actions;
export default socnetSlice.reducer;
